import React, { useCallback, useEffect, useMemo, useState } from "react";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";

import { resizableRoute } from "pages/_components/Resizable";
import { push, routerActions } from "react-router-redux";

import { AgendaPage, Button, Text } from "@ui-kit/components/index.es";

import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";

import { actions as agendaActions, selectors as agendaSelectors } from "reducers/frequentDestination";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as notificationActions } from "reducers/notification";
import { actions as transferActions } from "reducers/transfer";
import { actions as statusActions } from "reducers/status";
import { actions as layoutActions } from "reducers/layout";

import * as i18n from "util/i18n";
import getTransferPath from "util/transfers";
import { isUxDashboardImprovementsEnabled, isContactListUxEnabled } from "util/v3ShellLayoutSync";
import { getPostLoginHomePath } from "util/v3UxConfig";

import { getAccountByCbu } from "middleware/transfer";

function Agenda({
    location,
    isDesktop,
    fetching,
    fetchingExport,
    dispatch,
    contactList,
    idEnvironment,
    activeEnvironment,
    idContactDetail,
    initialSameHolder,
}) {
    const uxImprovementsDasboard = isUxDashboardImprovementsEnabled();
    const contactListUx = isContactListUxEnabled();
    const canManageFavorites = !!activeEnvironment.permissions["frequentDestination.manage"];
    const homePath = getPostLoginHomePath(uxImprovementsDasboard);

    useEffect(() => {
        if (activeEnvironment.permissions.frequentDestination) {
            dispatch(agendaActions.loadListRequest(idEnvironment, ""));
            dispatch(statusActions.saveLastHref(location));
        } else {
            dispatch(push(homePath));
        }
    }, [dispatch, idEnvironment, activeEnvironment.permissions.frequentDestination, location, homePath]);

    const handleBack = () => dispatch(push(homePath));

    const handleClickDownload = useCallback(() => dispatch(agendaActions.downloadListFrequentDestinations()), [
        dispatch,
    ]);

    const actionsCallbacks = (id, key, source) => {
        switch (key) {
            case "transfer":
                if (
                    activeEnvironment.permissions.transferToContact ||
                    activeEnvironment.permissions.transferThirdParties
                ) {
                    const contact = contactList.find(
                        ({ idFrequentDestination }) => String(idFrequentDestination) === String(id),
                    );
                    dispatch(
                        transferActions.loadThirdPartiesTransfer({
                            cbu: contact.cbu,
                            email: contact.email,
                            transferKind: contact.isOwn === "1" ? "same" : "other",
                            isFrequentDestinations: true,
                        }),
                    );
                    dispatch(
                        routerActions.push(
                            getTransferPath(
                                activeEnvironment.permissions.transferToContact ? "toContact" : "thirdParties",
                            ),
                        ),
                    );
                }
                break;
            case "modify":
                // Restore detail after modify only when the action started from the detail panel.
                // Context-menu modify must return to the list without reopening detail.
                dispatch(agendaActions.setIdContactDetail(source === "detail" ? String(id) : ""));
                dispatch(agendaActions.modifyFrequentDestinationPreRequest(id, true));
                break;
            case "delete":
                dispatch(agendaActions.deleteFrequentDestinationPreviewRequest(id));
                break;
            case "share":
                dispatch(
                    notificationActions.showNotification(i18n.get("agenda.action.success.share"), "success", [
                        "agenda",
                    ]),
                );
                break;
            case "copy":
                dispatch(
                    notificationActions.showNotification(i18n.get("agenda.action.success.copy"), "success", ["agenda"]),
                );
                break;
            default:
                break;
        }
    };

    const actionsLabels = (key) => {
        if (
            key === "transfer" &&
            !activeEnvironment.permissions.transferAccess &&
            !activeEnvironment.permissions.transferToContact &&
            !activeEnvironment.permissions.transferThirdParties
        ) {
            return "";
        }
        if ((key === "modify" || key === "delete") && !activeEnvironment.permissions["frequentDestination.manage"]) {
            return "";
        }
        return i18n.get(`agenda.contextMenu.${key}`);
    };

    const [aliasByCbu, setAliasByCbu] = useState({});
    const searchAlias = useCallback(
        async (cbucvu) => {
            if (Object.prototype.hasOwnProperty.call(aliasByCbu, cbucvu)) {
                return aliasByCbu[cbucvu];
            }
            const {
                data: { data },
            } = await getAccountByCbu(cbucvu);
            const alias = data.accountDetail?.cbuAlias || "";
            setAliasByCbu((current) => ({ ...current, [cbucvu]: alias }));
            return alias;
        },
        [aliasByCbu],
    );
    const handleFavorite = (id) => {
        const contact = contactList.find((item) => String(item.idFrequentDestination) === String(id));
        const isFavorite = contact?.isFavorite === "1" || contact?.isFavorite === true;
        dispatch(agendaActions.setFrequentDestinationFavoriteRequest(id, !isFavorite));
    };

    const downloadLabel = i18n.get("agenda.download");
    const legacyDownloadButton = (
        <Button
            icon="SendDown"
            iconVariant="outline"
            variant={isDesktop ? "outline" : "text"}
            color={isDesktop ? "primary" : "white"}
            onClick={handleClickDownload}
            isInactive={fetchingExport}
            loading={fetchingExport && isDesktop}
            aria-label={downloadLabel}
            text={isDesktop ? downloadLabel : undefined}
            w={isDesktop ? "200px" : undefined}
            size={isDesktop ? undefined : "large"}
            css={isDesktop ? undefined : { padding: 8 }}
        />
    );
    const mobileShellDownload = useMemo(
        () => (
            <Button
                type="button"
                variant="text"
                color="white"
                icon="SendDown"
                iconVariant="outline"
                size="large"
                css={{ padding: 8 }}
                aria-label={downloadLabel}
                onClick={handleClickDownload}
                isInactive={fetchingExport}
                loading={fetchingExport}
            />
        ),
        [downloadLabel, fetchingExport, handleClickDownload],
    );

    useEffect(() => {
        if (!uxImprovementsDasboard) {
            return undefined;
        }
        dispatch(layoutActions.setRightContentMobile(isDesktop ? null : mobileShellDownload));
        dispatch(layoutActions.setGoBackPath(homePath));
        dispatch(layoutActions.setTitleKey("agenda.title"));
        dispatch(layoutActions.setScopes(["agenda"]));
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setRightContentMobile(null));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setGoBackHandler(null));
            dispatch(layoutActions.setTitleKey(""));
        };
    }, [dispatch, uxImprovementsDasboard, isDesktop, mobileShellDownload, homePath]);

    const showLegacyHeader = !contactListUx || (!uxImprovementsDasboard && !isDesktop);

    return (
        <>
            {!uxImprovementsDasboard ? <Notification scopeToShow="agenda" /> : null}
            {!uxImprovementsDasboard ? <Notification scopeToShow="FrequentDestinations" /> : null}
            {showLegacyHeader ? (
                <div className="admin-detail-head px-5">
                    {isDesktop && <Head onBack={handleBack} />}
                    {(!uxImprovementsDasboard || isDesktop) && (
                        <Head
                            accessibilityTextId="agenda.title"
                            titleClassName={
                                !isDesktop ? "header-mobile-title-background-blue white-space-normal align-center" : ""
                            }
                            headerClassName={
                                !isDesktop
                                    ? "blue-main-header-mobile blue-main-title-mobile background-transparent"
                                    : ""
                            }
                            centerElement={() => (
                                <Text as="h1" className="m-0">
                                    {i18n.get("agenda.title")}
                                </Text>
                            )}
                            onBack={!isDesktop && handleBack}
                            rightContent={() => legacyDownloadButton}
                        />
                    )}
                </div>
            ) : null}
            <AgendaPage
                contactsArray={contactList.map((contact) => ({
                    id: String(contact.idFrequentDestination),
                    cbucvu: contact.cbu,
                    type: contact.productType,
                    bank: `${contact.localBank}${!contact.cbu.startsWith("000") ? `/${contact.subsidiaryBank}` : ""}`,
                    name: contact.recipientName,
                    reference: contact.name,
                    number: contact.recipientDocumentNumber,
                    email: contact.recipientEmail,
                    isOwn: contact.isOwn,
                    isFavorite: contact.isFavorite === "1" || contact.isFavorite === true,
                    document: `${contact.recipientDocumentType} ${contact.recipientDocumentNumber}`,
                    ...(Object.prototype.hasOwnProperty.call(aliasByCbu, contact.cbu)
                        ? { alias: aliasByCbu[contact.cbu] }
                        : {}),
                }))}
                getActions={actionsCallbacks}
                getActionsLabel={actionsLabels}
                getLabel={(key) => i18n.get(`agenda.detail.label.${key}`)}
                loading={fetching}
                defaultContactDetailID={idContactDetail}
                onAddContact={() => {
                    dispatch(agendaActions.resetListFrequentDestinations());
                    dispatch(push("/v2/agenda/create"));
                }}
                getAlias={searchAlias}
                initialValueFilterbySameTitular={contactListUx ? false : initialSameHolder}
                getFilterbySameTitular={(v) => dispatch(agendaActions.setSameHolder(v))}
                showAddContact={activeEnvironment.permissions["frequentDestination.manage"]}
                styleV3={contactListUx}
                showFavorite={contactListUx && canManageFavorites}
                onFavoriteClick={contactListUx && canManageFavorites ? handleFavorite : undefined}
                title={i18n.get("agenda.title")}
                detailTitle={i18n.get("agenda.detail.title")}
                onContactDetailChange={(id) => dispatch(agendaActions.setIdContactDetail(id ? String(id) : ""))}
                goBack={contactListUx ? handleBack : undefined}
                onDownload={contactListUx ? handleClickDownload : undefined}
                downloadLabel={i18n.get("agenda.download")}
            />
        </>
    );
}

Agenda.propTypes = {
    dispatch: func.isRequired,
    location: shape({
        pathname: string,
    }).isRequired,
    contactList: arrayOf(
        shape({
            name: string,
            recipientName: string,
            recipientDocumentType: string,
            recipientDocumentNumber: string,
            cbu: string,
            recipientEmail: string,
        }),
    ).isRequired,
    fetching: bool.isRequired,
    idEnvironment: number.isRequired,
    isDesktop: bool.isRequired,
    fetchingExport: bool.isRequired,
    activeEnvironment: shape({
        permissions: arrayOf(string),
    }).isRequired,
    idContactDetail: string.isRequired,
    initialSameHolder: string.isRequired,
};

const mapStateToProps = (state) => ({
    contactList: agendaSelectors.getListFrequentDestinations(state),
    idEnvironment: agendaSelectors.getEnviromentId(state),
    fetching: agendaSelectors.getFetching(state),
    fetchingExport: agendaSelectors.getFetchingExport(state),
    idContactDetail: agendaSelectors.getIdContactDetail(state),
    initialSameHolder: agendaSelectors.getSameHolder(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(Agenda);
